import React from 'react'
import { Outlet, useLocation } from 'react-router-dom'

import { Navigate } from '@lib/react-router-dom'

import { useResolvedAppTestProduct } from '@pages/ProductRegistration/AppropriatenessTest/hooks/useResolvedAppTestProduct'

import { useAccountReadContext } from '@context/AccountContextContext'

import { hasConsentedToLegalDocuments } from '@utils/AccountAccess/accountDocumentStatuses'
import {
  isEconomicProfileTestNeeded,
  isProductAppropriatenessInvalid,
  showApptestRiskScreen,
} from '@utils/AccountAccess/accountProductStatuses'
import {
  BasePaths,
  getProductAppTestRiskAcceptancePath,
  getProductAppropriatenessTestRiskPath,
  getProductEconomicProfilePath,
  getProductRegistrationPath,
} from '@utils/path'

export const ProductRegistrationGuard: React.FC = () => {
  const { account } = useAccountReadContext()
  const resolvedProductId = useResolvedAppTestProduct()
  const location = useLocation()

  const productRegistrationPath = getProductRegistrationPath(resolvedProductId)
  const legalDocumentsPath = `${productRegistrationPath}/legal-documents`
  const baseAppropriatenessRiskPath = `${BasePaths.TakeAppTest}/appropriateness-test/risk`
  const appTestRiskPath = getProductAppTestRiskAcceptancePath(resolvedProductId)
  const appropriatenessRiskPath = getProductAppropriatenessTestRiskPath(resolvedProductId)
  const economicProfilePath = getProductEconomicProfilePath(resolvedProductId)
  const pathname = location.pathname.replace(/\/$/, '')
  const guardedPaths = [
    BasePaths.TakeAppTest,
    `${BasePaths.TakeAppTest}/legal-documents`,
    `${BasePaths.TakeAppTest}/appropriateness-test`,
    baseAppropriatenessRiskPath,
    `${BasePaths.TakeAppTest}/app-test-risk`,
    `${BasePaths.TakeAppTest}/economic-profile-test`,
    productRegistrationPath,
    `${productRegistrationPath}/legal-documents`,
    `${productRegistrationPath}/appropriateness-test`,
    appropriatenessRiskPath,
    appTestRiskPath,
    economicProfilePath,
  ]
  const shouldGuardCurrentPath = guardedPaths.some((path) => pathname.endsWith(path))
  const isProductRegistrationPath = pathname.includes(BasePaths.TakeAppTest)
  const requiresLegalDocuments = !hasConsentedToLegalDocuments(account, resolvedProductId)
  const requiresEconomicProfile = isEconomicProfileTestNeeded(account, resolvedProductId)
  const requiresAppropriatenessTest = isProductAppropriatenessInvalid(resolvedProductId, account)
  let redirectPath: string | undefined

  if (
    requiresLegalDocuments &&
    isProductRegistrationPath &&
    !pathname.endsWith(legalDocumentsPath)
  ) {
    return <Navigate to={legalDocumentsPath} replace />
  }

  if (
    requiresAppropriatenessTest &&
    !showApptestRiskScreen(account, resolvedProductId) &&
    (pathname.endsWith(appTestRiskPath) ||
      pathname.endsWith(baseAppropriatenessRiskPath) ||
      pathname.endsWith(appropriatenessRiskPath) ||
      pathname.endsWith(economicProfilePath))
  ) {
    redirectPath = productRegistrationPath
  } else if (showApptestRiskScreen(account, resolvedProductId)) {
    redirectPath = requiresEconomicProfile
      ? appTestRiskPath
      : getProductAppropriatenessTestRiskPath(resolvedProductId)
  } else if (requiresEconomicProfile && !requiresAppropriatenessTest) {
    redirectPath = economicProfilePath
  }

  if (redirectPath && shouldGuardCurrentPath && !pathname.endsWith(redirectPath)) {
    return <Navigate to={redirectPath} replace />
  }

  return <Outlet />
}
